import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowUpRight, Check, Copy, Download, ExternalLink, FolderOpen, Globe, Image as ImageIcon, LayoutGrid,
  Link2, List, MapPin, MapPinOff, RefreshCw, Share2, Trash2, User, Users, X
} from 'lucide-react';
import PhotoDetail from '../components/PhotoDetail';
import { GRID, PhotoCard, PhotoGridSkeleton } from '../components/admin/PhotoGrid';
import {
  Badge, Button, Card, Checkbox, ConfirmDialog, cx, DateRangePicker, EmptyState, Field, FOCUS,
  IconButton, Modal, Notice, PageHeader, Progress, SearchInput, Segmented, Select, Spinner, StatRow, Tabs, TextInput
} from '../components/admin/ui';
import { formatBytes, formatDate, formatDateTime, toQuery, useApi, useDownload } from '../lib/admin';

const PAGE = 60;
const EMPTY_FILTERS = { q: '', region: '', user: '', folder: '', date_from: '', date_to: '', has_gps: '' };
const shareLink = (token) => `${window.location.origin}/share/${token}`;
const number = (n) => (n || 0).toLocaleString('es-PE');

// Active filters as removable chips: [{keys, text}]
function filterChips(filters, facets) {
  const chips = [];
  if (filters.q) chips.push({ keys: ['q'], text: `“${filters.q}”` });
  if (filters.region) chips.push({ keys: ['region'], text: filters.region });
  if (filters.user) chips.push({ keys: ['user', 'folder'], text: facets?.users?.find((u) => u.uid === filters.user)?.email || 'Usuario' });
  if (filters.folder) chips.push({ keys: ['folder'], text: facets?.folders?.find((f) => f.key === filters.folder)?.name || 'Folder' });
  if (filters.date_from || filters.date_to) {
    const text = [filters.date_from && formatDate(`${filters.date_from}T12:00:00`), filters.date_to && formatDate(`${filters.date_to}T12:00:00`)]
      .filter(Boolean).join(' – ');
    chips.push({ keys: ['date_from', 'date_to'], text });
  }
  if (filters.has_gps) chips.push({ keys: ['has_gps'], text: filters.has_gps === 'yes' ? 'Con GPS' : 'Sin GPS' });
  return chips;
}

// ── Share dataset ────────────────────────────────────────────────────────────

function ShareModal({ filters, facets, total, onClose }) {
  const api = useApi();
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [includeContributor, setIncludeContributor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const chips = filterChips(filters, facets);

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      setShare(await api('/admin/shares/', {
        method: 'POST',
        body: JSON.stringify({ name, filters: cleanFilters, expires_days: days, include_contributor: includeContributor }),
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(shareLink(share.token));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (share) {
    return (
      <Modal onClose={onClose} eyebrow="Enlace creado" title={share.name}>
        <p className="text-sm leading-relaxed text-umber/70">
          {number(share.photo_count)} fotos con su metadata y un CSV descargable, disponibles hasta el{' '}
          <span className="text-ink">{formatDate(share.expires_at)}</span>. Nunca incluye resultados de IA.
        </p>
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-umber/20 bg-paper p-1.5 pl-4">
          <Link2 className="h-4 w-4 flex-shrink-0 text-umber/45" strokeWidth={1.75} />
          <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-ink">{shareLink(share.token)}</span>
          <Button variant="primary" size="sm" icon={copied ? Check : Copy} onClick={copy}>{copied ? 'Copiado' : 'Copiar'}</Button>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <a href={shareLink(share.token)} target="_blank" rel="noreferrer"
            className={cx('inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm text-umber/70 hover:bg-umber/[0.06] hover:text-ink', FOCUS)}>
            Abrir <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Button variant="primary" onClick={onClose}>Listo</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} eyebrow="Compartir con otra empresa" title="Nuevo enlace al dataset">
      <div className="rounded-xl border border-umber/15 bg-paper px-4 py-3.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-serif text-2xl text-ink">{number(total)} <span className="text-base text-umber/60">fotos</span></span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-umber/45">congelado al crear</span>
        </div>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {chips.length ? chips.map((chip) => <Badge key={chip.keys.join()}>{chip.text}</Badge>) : <Badge>Todo el dataset</Badge>}
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <Field label="Nombre visible">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Dataset Cusco — octubre 2026" />
        </Field>
        <Field label="Disponible por">
          <Segmented
            value={days}
            onChange={setDays}
            className="w-full [&>button]:flex-1 [&>button]:justify-center"
            options={[7, 30, 90, 365].map((d) => ({ value: d, label: d === 365 ? '1 año' : `${d} días` }))}
          />
        </Field>
        <Checkbox
          checked={includeContributor}
          onChange={setIncludeContributor}
          label="Incluir el email de quien tomó cada foto"
          description="Si no, cada persona aparece con un código anónimo."
        />
      </div>

      {error && <div className="mt-4"><Notice>{error}</Notice></div>}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" icon={Link2} loading={saving} disabled={!total} onClick={create}>Crear enlace</Button>
      </div>
    </Modal>
  );
}

// ── Photos explorer ──────────────────────────────────────────────────────────

function PhotoTable({ photos, onOpen }) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-left text-[13px]">
          <thead>
            <tr className="border-b border-umber/15">
              {['', 'Usuario', 'Folder', 'Tomada', 'Ubicación', 'Dispositivo', 'Tamaño'].map((h, i) => (
                <th key={i} className="px-4 py-3 font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-umber/45">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {photos.map((p, i) => (
              <tr key={`${p.uid}-${p.id}`} onClick={() => onOpen(i)} className="cursor-pointer border-b border-umber/[0.08] transition-colors last:border-b-0 hover:bg-umber/[0.035]">
                <td className="w-16 py-2 pl-4 pr-0">
                  <img src={p.thumb_url || p.url} alt="" loading="lazy" className="h-12 w-10 rounded-md border border-umber/15 object-cover" />
                </td>
                <td className="px-4 py-2">
                  <p className="max-w-[220px] truncate text-ink">{p.user_email || p.uid}</p>
                  <p className="text-xs text-umber/50">{p.region || 'Sin región'}</p>
                </td>
                <td className="max-w-[200px] truncate px-4 py-2 text-umber/80">{p.folder_name || 'Sin nombre'}</td>
                <td className="whitespace-nowrap px-4 py-2 text-umber/80">{formatDateTime(p.taken_at)}</td>
                <td className="whitespace-nowrap px-4 py-2 font-mono text-[11.5px] tabular-nums text-umber/80">
                  {p.gps ? `${p.gps.latitude.toFixed(4)}, ${p.gps.longitude.toFixed(4)}` : <span className="text-umber/35">—</span>}
                </td>
                <td className="max-w-[180px] truncate px-4 py-2 text-umber/70">{p.device || p.camera || '—'}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-[11.5px] tabular-nums text-umber/60">{formatBytes(p.size_bytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PhotosTab() {
  const api = useApi();
  const download = useDownload();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [qDraft, setQDraft] = useState('');
  const [data, setData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState('grid');

  // Debounced search box
  useEffect(() => {
    const timer = setTimeout(() => setFilters((f) => (f.q === qDraft ? f : { ...f, q: qDraft })), 350);
    return () => clearTimeout(timer);
  }, [qDraft]);

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    setError('');
    try {
      const result = await api(`/admin/photos/${toQuery({ ...filters, limit: PAGE, fresh: fresh ? 1 : '' })}`);
      setData(result);
      setPhotos(result.photos);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const result = await api(`/admin/photos/${toQuery({ ...filters, limit: PAGE, offset: photos.length })}`);
      setPhotos((current) => [...current, ...result.photos]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await download(`/admin/photos/${toQuery({ ...filters, format: 'csv' })}`, 'chillaos_fotos.csv');
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const removeChip = (keys) => {
    if (keys.includes('q')) setQDraft('');
    setFilters((f) => ({ ...f, ...Object.fromEntries(keys.map((k) => [k, ''])) }));
  };
  const clearAll = () => {
    setQDraft('');
    setFilters(EMPTY_FILTERS);
  };

  const facets = data?.facets;
  const summary = data?.summary;
  const chips = filterChips(filters, facets);

  const regionOptions = useMemo(() => [
    { value: '', label: 'Todas las regiones' },
    ...(facets?.regions || []).map((r) => ({ value: r.name, label: r.name, hint: r.count || '—' })),
  ], [facets]);
  const userOptions = useMemo(() => [
    { value: '', label: 'Todos los usuarios' },
    ...(facets?.users || []).map((u) => ({ value: u.uid, label: u.email || u.uid, description: u.region || 'Sin región', hint: u.count })),
  ], [facets]);
  const folderOptions = useMemo(() => [
    { value: '', label: 'Todos los folders' },
    ...(facets?.folders || [])
      .filter((f) => !filters.user || f.key.startsWith(`${filters.user}/`))
      .map((f) => ({ value: f.key, label: f.name || 'Sin nombre', description: f.user_email, hint: f.count })),
  ], [facets, filters.user]);

  const index = selected === null ? -1 : selected;

  return (
    <div className="space-y-6">
      {summary && (
        <StatRow items={[
          { label: 'Fotos', value: number(summary.photos), sub: chips.length ? `de ${number(data.overall.photos)} en total` : 'en el archivo' },
          { label: 'Contribuyentes', value: number(summary.contributors) },
          { label: 'Regiones', value: summary.regions, sub: 'de 25' },
          { label: 'Con GPS', value: `${summary.photos ? Math.round((summary.with_gps / summary.photos) * 100) : 0}%`, sub: `${number(summary.with_gps)} fotos` },
          { label: 'Volumen', value: formatBytes(summary.bytes) },
        ]} />
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-[minmax(240px,1.6fr)_1fr_1.2fr_1.2fr]">
          <SearchInput value={qDraft} onChange={(e) => setQDraft(e.target.value)} onClear={() => setQDraft('')} placeholder="Buscar email, folder, cámara, dispositivo o id" />
          <Select value={filters.region} onChange={(v) => setFilter('region', v)} options={regionOptions} icon={Globe} searchable />
          <Select value={filters.user} onChange={(v) => setFilters((f) => ({ ...f, user: v, folder: '' }))} options={userOptions} icon={User} searchable />
          <Select value={filters.folder} onChange={(v) => setFilter('folder', v)} options={folderOptions} icon={FolderOpen} searchable align="right" />
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
          <DateRangePicker
            className="w-full sm:w-64"
            from={filters.date_from}
            to={filters.date_to}
            onChange={({ from, to }) => setFilters((f) => ({ ...f, date_from: from, date_to: to }))}
          />
          <Segmented
            value={filters.has_gps}
            onChange={(v) => setFilter('has_gps', v)}
            options={[
              { value: '', label: 'Todas' },
              { value: 'yes', label: 'Con GPS', icon: MapPin },
              { value: 'no', label: 'Sin GPS', icon: MapPinOff },
            ]}
          />
          <div className="flex-1" />
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: 'grid', label: '', icon: LayoutGrid, title: 'Cuadrícula' },
              { value: 'list', label: '', icon: List, title: 'Lista' },
            ]}
          />
          <IconButton icon={RefreshCw} label="Actualizar" variant="secondary" onClick={() => load(true)} className={loading ? '[&>svg]:animate-spin' : ''} />
          <Button icon={Download} loading={exporting} disabled={!data?.total} onClick={exportCsv}>CSV</Button>
          <Button variant="primary" icon={Share2} disabled={!data?.total} onClick={() => setSharing(true)}>Compartir</Button>
        </div>
      </Card>

      {error && <Notice onClose={() => setError('')}>{error}</Notice>}

      {/* Results */}
      {data && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="mr-2 text-sm text-umber/60">
            <span className="font-serif text-xl text-ink">{number(data.total)}</span> foto{data.total !== 1 ? 's' : ''}
          </p>
          {chips.map((chip) => (
            <button key={chip.keys.join()} type="button" onClick={() => removeChip(chip.keys)}
              className={cx('inline-flex items-center gap-1.5 rounded-full border border-umber/20 bg-vellum py-1 pl-3 pr-2 text-xs text-ink transition-colors hover:border-umber/45', FOCUS)}>
              {chip.text} <X className="h-3 w-3 text-umber/50" />
            </button>
          ))}
          {chips.length > 0 && (
            <button type="button" onClick={clearAll} className="px-1 text-xs text-umber/55 underline-offset-4 hover:text-ink hover:underline">
              Limpiar todo
            </button>
          )}
        </div>
      )}

      {loading && !photos.length ? (
        <PhotoGridSkeleton />
      ) : !photos.length ? (
        <EmptyState icon={ImageIcon} title={chips.length ? 'Sin coincidencias' : 'Todavía no hay fotos'}
          action={chips.length ? <Button onClick={clearAll}>Limpiar filtros</Button> : null}>
          {chips.length ? 'Ninguna foto coincide con estos filtros.' : 'Las fotos que se suban desde la app aparecerán aquí con toda su metadata.'}
        </EmptyState>
      ) : (
        <>
          {view === 'grid' ? (
            <div className={GRID}>
              {photos.map((p, i) => (
                <PhotoCard
                  key={`${p.uid}-${p.id}`}
                  photo={p}
                  onClick={() => setSelected(i)}
                  title={p.user_email || p.uid}
                  subtitle={`${p.region || 'Sin región'} · ${formatDate(p.taken_at)}`}
                />
              ))}
            </div>
          ) : (
            <PhotoTable photos={photos} onOpen={setSelected} />
          )}
          {photos.length < data.total && (
            <div className="flex justify-center pt-2">
              <Button loading={loadingMore} onClick={loadMore}>Cargar {Math.min(PAGE, data.total - photos.length)} más</Button>
            </div>
          )}
        </>
      )}

      {index >= 0 && photos[index] && (
        <PhotoDetail
          photo={photos[index]}
          position={`${index + 1} / ${data.total}`}
          onClose={() => setSelected(null)}
          onPrev={index > 0 ? () => setSelected(index - 1) : null}
          onNext={index < photos.length - 1 ? () => setSelected(index + 1) : null}
        >
          <Link
            to={`/admin/folders/${photos[index].uid}/${photos[index].folder_id}`}
            className={cx('mt-4 flex items-center justify-between rounded-xl border border-umber/15 px-4 py-3 text-sm text-ink transition-colors hover:border-umber/40', FOCUS)}
          >
            <span className="flex items-center gap-2.5"><FolderOpen className="h-4 w-4 text-umber/55" strokeWidth={1.75} /> Abrir folder y análisis IA</span>
            <ArrowUpRight className="h-4 w-4 text-umber/45" strokeWidth={1.75} />
          </Link>
        </PhotoDetail>
      )}
      {sharing && <ShareModal filters={filters} facets={facets} total={data?.total || 0} onClose={() => setSharing(false)} />}
    </div>
  );
}

// ── App folders of every user ────────────────────────────────────────────────

function FoldersTab() {
  const api = useApi();
  const [folders, setFolders] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    api('/admin/folders/').then((r) => setFolders(r.folders)).catch((e) => setError(e.message));
  }, [api]);

  const stateOf = (f) => (f.ai.processing > 0 ? 'processing' : f.ai.total === 0 ? 'pending' : f.ai.completed >= f.photo_count ? 'done' : 'partial');
  const shown = (folders || []).filter((f) =>
    `${f.name} ${f.user_email} ${f.region}`.toLowerCase().includes(query.toLowerCase())
    && (!status || stateOf(f) === status || (status === 'pending' && stateOf(f) === 'partial')));

  if (error) return <Notice>{error}</Notice>;
  if (!folders) return <div className="flex justify-center py-24"><Spinner /></div>;
  if (!folders.length) {
    return <EmptyState icon={FolderOpen} title="Sin folders todavía">Los folders que creen los usuarios en la app aparecerán aquí.</EmptyState>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <SearchInput className="sm:w-80" value={query} onChange={(e) => setQuery(e.target.value)} onClear={() => setQuery('')} placeholder="Buscar folder, usuario o región" />
        <Segmented
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: 'Todos' },
            { value: 'pending', label: 'Por analizar' },
            { value: 'processing', label: 'En proceso' },
            { value: 'done', label: 'Transcritos' },
          ]}
        />
      </div>

      <Card className="overflow-hidden">
        <div className="hidden grid-cols-[1.5fr_1.3fr_110px_80px_120px_190px_24px] gap-4 border-b border-umber/15 px-5 py-3 md:grid">
          {['Folder', 'Usuario', 'Región', 'Fotos', 'Última foto', 'Análisis IA', ''].map((h) => (
            <span key={h} className="font-mono text-[10px] uppercase tracking-[0.2em] text-umber/45">{h}</span>
          ))}
        </div>
        {shown.length === 0 && <p className="px-5 py-12 text-center text-sm text-umber/50">Ningún folder coincide.</p>}
        {shown.map((f) => {
          const state = stateOf(f);
          return (
            <Link
              key={`${f.uid}/${f.id}`}
              to={`/admin/folders/${f.uid}/${f.id}`}
              className={cx('group grid items-center gap-1 border-b border-umber/[0.08] px-5 py-4 transition-colors last:border-b-0 hover:bg-umber/[0.035] md:grid-cols-[1.5fr_1.3fr_110px_80px_120px_190px_24px] md:gap-4', FOCUS)}
            >
              <span className="truncate font-serif text-lg leading-tight text-ink">{f.name || <span className="italic text-umber/50">Sin nombre</span>}</span>
              <span className="truncate text-[13px] text-umber/75">{f.user_email || f.uid}</span>
              <span className="text-[13px] text-umber/65">{f.region || '—'}</span>
              <span className="font-mono text-[12px] tabular-nums text-umber/75">{f.photo_count}</span>
              <span className="text-[13px] text-umber/60">{formatDate(f.last_photo_at || f.created_at)}</span>
              <span className="flex items-center gap-3">
                <span className="w-20"><Progress value={f.ai.completed} total={f.photo_count} tone={state === 'processing' ? 'warning' : 'ink'} /></span>
                {state === 'processing' && <Badge tone="warning" dot>{f.ai.completed}/{f.photo_count}</Badge>}
                {state === 'done' && <Badge tone="success" dot>Transcrito</Badge>}
                {state === 'partial' && <Badge dot>{f.ai.completed}/{f.photo_count}</Badge>}
                {state === 'pending' && <Badge>Sin analizar</Badge>}
              </span>
              <ArrowUpRight className="hidden h-4 w-4 text-umber/30 transition-colors group-hover:text-ink md:block" strokeWidth={1.75} />
            </Link>
          );
        })}
      </Card>
    </div>
  );
}

// ── Shared links ─────────────────────────────────────────────────────────────

const SHARE_BADGE = {
  active: { tone: 'success', label: 'Activo' },
  expired: { tone: 'neutral', label: 'Expirado' },
  revoked: { tone: 'danger', label: 'Revocado' },
};

function SharesTab() {
  const api = useApi();
  const [shares, setShares] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(() => {
    api('/admin/shares/').then((r) => setShares(r.shares)).catch((e) => setError(e.message));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const copy = (token) => {
    navigator.clipboard.writeText(shareLink(token));
    setCopied(token);
    setTimeout(() => setCopied(''), 1500);
  };

  if (error) return <Notice>{error}</Notice>;
  if (!shares) return <div className="flex justify-center py-24"><Spinner /></div>;
  if (!shares.length) {
    return (
      <EmptyState icon={Share2} title="Aún no compartes datasets">
        En la pestaña Fotos, filtra lo que quieras entregar y pulsa “Compartir”. Obtendrás un enlace público con caducidad.
      </EmptyState>
    );
  }

  const describe = (filters) => Object.entries(filters || {}).filter(([, v]) => v).map(([k, v]) => {
    if (k === 'has_gps') return v === 'yes' ? 'Con GPS' : 'Sin GPS';
    if (k === 'user' || k === 'folder') return k === 'user' ? 'Un usuario' : 'Un folder';
    if (k === 'date_from') return `Desde ${v}`;
    if (k === 'date_to') return `Hasta ${v}`;
    return k === 'q' ? `“${v}”` : v;
  });

  return (
    <div className="space-y-3">
      {shares.map((s) => {
        const badge = SHARE_BADGE[s.status];
        const chips = describe(s.filters);
        return (
          <Card key={s.token} className={cx('flex flex-col gap-4 px-5 py-4 md:flex-row md:items-center', s.status !== 'active' && 'opacity-70')}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2.5">
                <p className="truncate font-serif text-xl text-ink">{s.name}</p>
                <Badge tone={badge.tone} dot>{badge.label}</Badge>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Badge tone="dark">{number(s.photo_count)} fotos</Badge>
                {(chips.length ? chips : ['Todo el dataset']).map((c) => <Badge key={c}>{c}</Badge>)}
                {s.include_contributor && <Badge tone="warning">Con emails</Badge>}
              </div>
              <p className="mt-2.5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-umber/45">
                {formatDate(s.created_at)} · {s.created_by} · expira {formatDate(s.expires_at)} · {s.access_count} visita{s.access_count !== 1 ? 's' : ''}
              </p>
            </div>
            {s.status === 'active' && (
              <div className="flex items-center gap-1.5">
                <Button size="sm" icon={copied === s.token ? Check : Copy} onClick={() => copy(s.token)}>{copied === s.token ? 'Copiado' : 'Copiar enlace'}</Button>
                <a href={shareLink(s.token)} target="_blank" rel="noreferrer" title="Abrir"
                  className={cx('inline-flex h-8 w-8 items-center justify-center rounded-lg text-umber/60 hover:bg-umber/[0.06] hover:text-ink', FOCUS)}>
                  <ExternalLink className="h-4 w-4" strokeWidth={1.75} />
                </a>
                <IconButton icon={Trash2} size="sm" label="Revocar" onClick={() => setRevoking(s)} className="hover:!text-maroon" />
              </div>
            )}
          </Card>
        );
      })}
      {revoking && (
        <ConfirmDialog
          title="Revocar enlace"
          confirmLabel="Revocar"
          onClose={() => setRevoking(null)}
          onConfirm={async () => {
            await api(`/admin/shares/${revoking.token}/`, { method: 'DELETE' });
            load();
          }}
        >
          Quien tenga el enlace de “{revoking.name}” ya no podrá ver las fotos ni descargar el CSV. Esta acción no se puede deshacer.
        </ConfirmDialog>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'photos', label: 'Fotos', icon: ImageIcon },
  { key: 'folders', label: 'Folders de la app', icon: Users },
  { key: 'shares', label: 'Enlaces compartidos', icon: Link2 },
];

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'photos';

  return (
    <div className="min-h-screen bg-paper font-ui text-ink">
      <div className="mx-auto max-w-7xl px-4 pb-24 pt-10 md:px-10 md:pt-14">
        <PageHeader
          eyebrow="Panel de administración"
          title={<>Archivo de <span className="italic text-maroon">campo</span></>}
          description="Todo lo que los usuarios fotografían con la app, con su ubicación y metadata. Solo los administradores pueden iniciar el análisis de IA."
        />
        <div className="mb-8 mt-8">
          <Tabs tabs={TABS} value={tab} onChange={(key) => setSearchParams({ tab: key })} />
        </div>
        {tab === 'photos' && <PhotosTab />}
        {tab === 'folders' && <FoldersTab />}
        {tab === 'shares' && <SharesTab />}
      </div>
    </div>
  );
}
